import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import DealerTrendsAddModal from './DealerTrendsAddModal';
import {
  setDealerTrendsPeriod,
  setDealerTrendsView,
  setDealerTrendsComparisonType,
  setSelectedDealers,
  updateDealerPreferences,
} from '../../../store/slices/dashboard';
import { getDataWAuth } from '../../../utils/api-service';
import { CCM_DASHBOARD_DEALER_TRENDS, USER_PREFERENCE } from '../../../constants/constant';
import styles from './DealerTrendsTable.module.css';

const DealerTrendsTable = () => {
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.userState.userData);
  const { period, view, comparisonType, selectedDealers } = useSelector((state) => state.bpDashboard.dealerTrends);
  const [modalOpen, setModalOpen] = useState(false);
  const [trendsData, setTrendsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  useEffect(() => {
    if (userData && userData.country) {
      loadDealerPreferences();
    }
  }, [userData?.country]);
  useEffect(() => {
    if (preferencesLoaded && selectedDealers && selectedDealers.length > 0) {
      fetchTrendsData();
    } else if (preferencesLoaded) {
      setTrendsData(null);
    }
  }, [selectedDealers, period, comparisonType, preferencesLoaded]);

  const loadDealerPreferences = async () => {
    try {
      const response = await getDataWAuth(USER_PREFERENCE);
      const userData = response?.data?.data || response?.data || response;
      
      if (userData && userData.preferedDealers) {
        const dealerIdsStr = userData.preferedDealers.split(',').filter(id => id.trim());
        
        if (dealerIdsStr.length > 0) {
          const dealerIds = dealerIdsStr.map(id => id.trim());
          const url = `${CCM_DASHBOARD_DEALER_TRENDS}?dealerIds=${dealerIds.join(',')}&period=${period}`;
          const trendsResponse = await getDataWAuth(url);
          const trendsDataResponse = trendsResponse?.data || trendsResponse;
          
          if (trendsDataResponse && trendsDataResponse.dealers) {
            const dealers = trendsDataResponse.dealers.map(dealer => ({
              dealerId: dealer.dealerId,
              dealerName: dealer.dealerName
            }));
            
            dispatch(setSelectedDealers(dealers));
          }
        }
      }
    } catch (error) {
      console.error('Error loading dealer preferences:', error);
    } finally {
      setPreferencesLoaded(true);
    }
  };

  const fetchTrendsData = async () => {
    setLoading(true);
    try {
      const dealerIds = selectedDealers.map(d => d.dealerId).join(',');
      const url = `${CCM_DASHBOARD_DEALER_TRENDS}?dealerIds=${dealerIds}&period=${period}&comparisonType=${comparisonType}`;
      
      const response = await getDataWAuth(url);
      const data = response?.data || response;
      
      setTrendsData(data);
    } catch (error) {
      console.error('Error fetching dealer trends:', error);
      setTrendsData(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (event, newPeriod) => {
    if (newPeriod !== null) {
      dispatch(setDealerTrendsPeriod(newPeriod));
    }
  };

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      dispatch(setDealerTrendsView(newView));
    }
  };

  const handleComparisonTypeChange = (event, newComparisonType) => {
    if (newComparisonType !== null) {
      dispatch(setDealerTrendsComparisonType(newComparisonType));
    }
  };

  const handleAddDealers = async (dealers) => {
    dispatch(setSelectedDealers(dealers));
    setModalOpen(false);
    try {
      if (userData && userData.country) {
        const dealerIds = dealers.map(d => d.dealerId);
        await dispatch(updateDealerPreferences({ dealerIds, country: userData.country }));
      }
    } catch (error) {
      console.error('Error saving dealer preferences:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getTrendIcon = (trend, trendPercent) => {
    if (trend === 'up') {
      return (
        <Box className={styles.trendContainer}>
          <TrendingUpIcon className={styles.trendIconUp} />
          <Typography className={styles.trendPercentUp}>{trendPercent}%</Typography>
        </Box>
      );
    } else if (trend === 'down') {
      return (
        <Box className={styles.trendContainer}>
          <TrendingDownIcon className={styles.trendIconDown} />
          <Typography className={styles.trendPercentDown}>{trendPercent}%</Typography>
        </Box>
      );
    } else {
      return (
        <Box className={styles.trendContainer}>
          <TrendingFlatIcon className={styles.trendIconFlat} />
          <Typography className={styles.trendPercentFlat}>{trendPercent}%</Typography>
        </Box>
      );
    }
  };

  const renderMonthOverMonthTable = () => {
    return (
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.headerCell}>
              <Box className={styles.headerContent}>
                <Typography className={styles.headerText}></Typography>
              </Box>
            </th>
            {trendsData.dealers.map((dealer) => (
              <th key={dealer.dealerId} className={styles.headerCell}>
                <Box className={styles.headerContent}>
                  <Typography className={styles.dealerName}>{dealer.dealerName}</Typography>
                </Box>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {trendsData.months && [...trendsData.months].reverse().map((month) => (
            <tr key={month} className={styles.dataRow}>
              <td className={styles.monthCell}>
                <Typography className={styles.monthText}>{month}</Typography>
              </td>
              {trendsData.dealers.map((dealer) => {
                const monthData = dealer.monthlyAmounts?.find((d) => d.month === month);
                
                if (!monthData) {
                  return (
                    <td key={dealer.dealerId} className={styles.dataCell}>
                      <Typography className={styles.dataText}>0</Typography>
                    </td>
                  );
                }
                const displayValue = view === 'amount'
                  ? monthData.amount > 0 && monthData.currency
                    ? `${formatCurrency(monthData.amount)} ${monthData.currency}`
                    : formatCurrency(monthData.amount)
                  : monthData.volume || 0;
                
                // Only show trend when view is 'amount'
                const hasTrend = view === 'amount' && monthData.trendPercentage !== null && monthData.trendPercentage !== undefined;

                return (
                  <td key={dealer.dealerId} className={styles.dataCell}>
                    {hasTrend ? (
                      <Box className={styles.cellWithTrend}>
                        {getTrendIcon(monthData.trendDirection, Math.abs(monthData.trendPercentage).toFixed(1))}
                        <Typography className={styles.dataText}>{displayValue}</Typography>
                      </Box>
                    ) : (
                      <Typography className={`${styles.dataText} ${styles.dataTextCentered}`}>{displayValue}</Typography>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderYearOverYearTable = () => {
    const years = trendsData.years || ['2025', '2024'];
    
    return (
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.headerCell} rowSpan="2">
              <Box className={styles.headerContent}>
                <Typography className={styles.headerText}>Month</Typography>
              </Box>
            </th>
            <th className={styles.headerCell} rowSpan="2">
              <Box className={styles.headerContentCenter}>
                <Typography className={styles.headerTextCenter}>Year</Typography>
              </Box>
            </th>
            {trendsData.dealers.map((dealer) => (
              <th key={dealer.dealerId} className={styles.headerCellYOY}>
                <Box className={styles.headerContent}>
                  <Typography className={styles.dealerName}>{dealer.dealerName}</Typography>
                </Box>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {trendsData.months && [...trendsData.months].reverse().map((monthName, idx) => {
            // Group rows for this month
            return (
              <React.Fragment key={`month-group-${monthName}`}>
                {/* Year rows */}
                {years.map((year, yearIdx) => (
                  <tr key={`${monthName}-${year}`} className={yearIdx === 0 ? styles.yearRowFirst : styles.yearRow}>
                    {yearIdx === 0 && (
                      <td className={styles.monthCellYOY} rowSpan={years.length}>
                        <Typography className={styles.monthTextYOY}>{monthName}</Typography>
                      </td>
                    )}
                    <td className={styles.yearCellYOY}>
                      <Typography className={styles.yearTextYOY}>{year}</Typography>
                    </td>
                    {trendsData.dealers.map((dealer) => {
                      // Find data for this month and year
                      const monthData = dealer.monthlyAmounts?.find(
                        (d) => d.year === year && (d.month.startsWith(monthName) || d.month === '')
                      );
                      
                      if (!monthData) {
                        return (
                          <td key={`${dealer.dealerId}-${year}`} className={styles.dataCellYOY}>
                            <Typography className={styles.dataTextYOY}>0</Typography>
                          </td>
                        );
                      }

                      const displayValue = view === 'amount'
                        ? monthData.amount > 0 && monthData.currency
                          ? `${formatCurrency(monthData.amount)} ${monthData.currency}`
                          : formatCurrency(monthData.amount || 0)
                        : monthData.volume || 0;
                      
                      // Only show trend on current year (first year) and when view is 'amount'
                      const hasTrend = view === 'amount' && yearIdx === 0 && monthData.trendPercentage !== null && monthData.trendPercentage !== undefined;

                      return (
                        <td key={`${dealer.dealerId}-${year}`} className={styles.dataCellYOY}>
                          {hasTrend ? (
                            <Box className={styles.yoyDataContainerWithTrend}>
                              <Box className={styles.trendContainerYOY}>
                                {monthData.trendDirection === 'up' ? (
                                  <>
                                    <TrendingUpIcon className={styles.trendIconUp} />
                                    <Typography className={styles.trendPercentUp}>{Math.abs(monthData.trendPercentage).toFixed(1)}%</Typography>
                                  </>
                                ) : monthData.trendDirection === 'down' ? (
                                  <>
                                    <TrendingDownIcon className={styles.trendIconDown} />
                                    <Typography className={styles.trendPercentDown}>{Math.abs(monthData.trendPercentage).toFixed(1)}%</Typography>
                                  </>
                                ) : (
                                  <>
                                    <TrendingFlatIcon className={styles.trendIconFlat} />
                                    <Typography className={styles.trendPercentFlat}>{Math.abs(monthData.trendPercentage).toFixed(1)}%</Typography>
                                  </>
                                )}
                              </Box>
                              <Typography className={styles.dataTextYOY}>{displayValue}</Typography>
                            </Box>
                          ) : yearIdx === 0 ? (
                            <Typography className={styles.dataTextYOYAlignRight}>{displayValue}</Typography>
                          ) : (
                            <Box className={styles.yoyDataContainerWithTrend}>
                              <Box className={styles.trendContainerYOY} style={{ visibility: 'hidden' }}>
                                <TrendingUpIcon className={styles.trendIconUp} />
                                <Typography className={styles.trendPercentUp}>0.0%</Typography>
                              </Box>
                              <Typography className={styles.dataTextYOY}>{displayValue}</Typography>
                            </Box>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <Box className={styles.container}>
      <Box className={styles.headerSection}>
        <Typography variant="h6" className={styles.title}>
          Dealer Trends
        </Typography>

        <Box className={styles.controlsSection}>
          <ToggleButtonGroup
            value={period}
            exclusive
            onChange={handlePeriodChange}
            className={styles.toggleGroup}
            size="small"
          >
            <ToggleButton value="3M" className={styles.toggleButton}>
              3M
            </ToggleButton>
            <ToggleButton value="6M" className={styles.toggleButton}>
              6M
            </ToggleButton>
            <ToggleButton value="12M" className={styles.toggleButton}>
              12M
            </ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            value={view}
            exclusive
            onChange={handleViewChange}
            className={styles.toggleGroup}
            size="small"
          >
            <ToggleButton value="amount" className={styles.toggleButton}>
              Value
            </ToggleButton>
            <ToggleButton value="volume" className={styles.toggleButton}>
              Count
            </ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            value={comparisonType}
            exclusive
            onChange={handleComparisonTypeChange}
            className={styles.toggleGroup}
            size="small"
          >
            <ToggleButton value="MOM" className={styles.toggleButton}>
              Month
            </ToggleButton>
            <ToggleButton value="YOY" className={styles.toggleButton}>
              Year
            </ToggleButton>
          </ToggleButtonGroup>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setModalOpen(true)}
            className={styles.addButton}
          >
            Add Dealer
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box className={styles.loadingState}>
          <CircularProgress size={50} />
          <Typography className={styles.loadingText}>Loading dealer trends...</Typography>
        </Box>
      ) : trendsData && trendsData.dealers && trendsData.dealers.length > 0 ? (
        <Box className={styles.tableWrapper}>
          {trendsData.comparisonType === 'YOY' ? renderYearOverYearTable() : renderMonthOverMonthTable()}
        </Box>
      ) : (
        <Box className={styles.emptyState}>
          <Typography className={styles.emptyText}>
            Click "Add Dealer" to select dealers and view trends
          </Typography>
        </Box>
      )}
      <DealerTrendsAddModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleAddDealers}
        currentDealers={selectedDealers}
      />
    </Box>
  );
};

export default DealerTrendsTable;
